import { useTranslation } from "react-i18next";

import { AnalysisRunStatus } from "../../DocumentAnalysisAPI";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * How a run's outcome reads.
 *
 * Five words, and the distinctions carry weight. `undecided` is grey rather than red: the pipeline
 * worked and the answer was "none of these roles", which is a finding about the catalogue and not a
 * fault. `failed` and `aborted` are both red but never merged — one is a defect to look into, the
 * other is somebody having changed their mind. `running` exists because a record is opened when a
 * run starts, and a run whose server died must not read as one that finished.
 */

const TONE: Record<string, string> = {
  completed: "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200",
  undecided: "bg-muted text-muted-foreground",
  failed: "bg-destructive/10 text-destructive",
  aborted: "bg-destructive/10 text-destructive",
  running: "bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-200",
};

export function StatusBadge({
  status,
  failedStep,
}: {
  status: AnalysisRunStatus | string;
  failedStep?: string | null;
}) {
  const { t } = useTranslation();

  const badge = (
    <span
      className={`inline-flex whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium ${
        TONE[status] ?? "bg-muted text-muted-foreground"
      }`}
    >
      {t(`documentAnalysisPanel.analyses.statusName.${status}`)}
    </span>
  );

  // Which step failed is the first question about a failure, and it belongs on the badge rather
  // than one click away.
  if (!failedStep) return badge;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span>{badge}</span>
        </TooltipTrigger>
        <TooltipContent>{t(`documentAnalysisPanel.test.steps.${failedStep}`)}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
